'use client'

import type { Newsletter } from '@snowball-tech/common/types'

import useNewsletter from '@snowball-tech/common/hooks/useNewsletter'
import useTranslations from '@snowball-tech/common/hooks/useTranslations'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { CuteIcon } from '@snowball-tech/fractal/dist/src/components/CuteIcon'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilCheck as CheckIcon } from '@tooni/iconscout-unicons-react'

import { useCallback, useState } from 'react'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'

import useApi from '@/hooks/useApi'

type Props = {
  hasSummary: boolean
  isUserConnected: boolean
  isUserPaying: boolean
  isUserSubscribedToNewsletter: boolean
  newsletter: Newsletter
  hasTimeToRead?: boolean
}
export default function EditionHeaderBlockNewsletterDescription({
  hasSummary,
  hasTimeToRead = true,
  isUserConnected,
  isUserPaying,
  isUserSubscribedToNewsletter,
  newsletter,
}: Props) {
  const $t = useTranslations()

  const endpoints = useApi()

  const { description, hasDescription } = useNewsletter(newsletter, {
    fullQualifiedUrl: false,
  })

  const canSubscribe =
    !isEmpty(newsletter.uuid) &&
    isUserConnected &&
    !isUserSubscribedToNewsletter &&
    (!newsletter.bundled || isUserPaying)

  const [isSubscribing, setIsSubscribing] = useState(false)
  const [hasSubscribed, setHasSubscribed] = useState(false)
  const handleSubscribe = useCallback(async () => {
    if (isEmpty(newsletter.uuid) || !canSubscribe) {
      return
    }

    setIsSubscribing(true)

    await endpoints.newsletters.subscriptions.add(newsletter.uuid)

    setIsSubscribing(constant(false))
    setHasSubscribed(true)
  }, [canSubscribe, endpoints.newsletters.subscriptions, newsletter.uuid])

  return (
    <div
      className={cj(
        'flex flex-col items-center gap-3 bg-decorative-yellow-90 p-3 text-center',
        hasSummary ? 'border-t-1' : 'rounded-t-sm',
        hasTimeToRead ? '' : 'rounded-b-sm',
      )}
    >
      {hasDescription && (
        <Typography variant="body-2">{description}</Typography>
      )}

      <Typography variant="caption-median">
        {newsletter.bundled
          ? $t.rich('newsletter.bundled.description')
          : $t.rich('newsletter.free.description')}
      </Typography>

      {canSubscribe &&
        (hasSubscribed ? (
          <div className="flex flex-row items-center justify-center gap-1">
            <CuteIcon color="purple" icon={<CheckIcon />} />

            <Typography variant="body-1-median">
              {$t('newsletter.subscribed.success')}
            </Typography>
          </div>
        ) : (
          <Button
            disabled={isSubscribing}
            label={$t('newsletter.subscribe.label')}
            variant="display"
            onClick={handleSubscribe}
          />
        ))}
    </div>
  )
}
